import React, { useState, useEffect } from 'react';
import Button from './Button';
import { Modal } from './Modal';
import { Badge } from './Badge';
import { EmptyState } from './EmptyState';
import LoadingSpinner from './LoadingSpinner';
import Alert from './Alert';
import ConfirmDialog from './ConfirmDialog';
import { roomsApi, handleApiError, type Room } from '../services/api';
import { Building2, MapPin, Users, Plus, Edit2, Trash2, Monitor } from 'lucide-react';

interface RoomsConfigurationProps {
  eventId: string;
}

interface RoomFormData {
  name: string;
  description: string;
  capacity: number;
  location: string;
  equipment: string[];
  isActive: boolean;
  virtualLink: string;
  virtualRoomId: string;
}

const equipmentOptions = [
  'Projector',
  'Screen',
  'Whiteboard',
  'Flipchart',
  'Microphone',
  'Speakers',
  'Computer',
  'WiFi',
  'Power Outlets',
  'Air Conditioning',
  'Video Conference System',
  'Camera',
  'Recording Equipment'
];

export const RoomsConfiguration: React.FC<RoomsConfigurationProps> = ({ eventId }) => {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [deletingRoom, setDeletingRoom] = useState<Room | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  const [formData, setFormData] = useState<RoomFormData>({
    name: '',
    description: '',
    capacity: 1,
    location: '',
    equipment: [],
    isActive: true,
    virtualLink: '',
    virtualRoomId: ''
  });

  // Load rooms for the event
  const loadRooms = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await roomsApi.getByEventId(eventId);
      setRooms(response.rooms);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRooms();
  }, [eventId]);

  // Reset form data
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      capacity: 1,
      location: '',
      equipment: [],
      isActive: true,
      virtualLink: '',
      virtualRoomId: ''
    });
  };

  // Handle form input changes
  const handleInputChange = (field: keyof RoomFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle equipment selection
  const handleEquipmentToggle = (equipment: string) => {
    setFormData(prev => ({
      ...prev,
      equipment: prev.equipment.includes(equipment)
        ? prev.equipment.filter(e => e !== equipment)
        : [...prev.equipment, equipment]
    }));
  };

  // Handle create room
  const handleCreateRoom = async () => {
    try {
      setFormLoading(true);
      setError(null);

      const roomData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        capacity: formData.capacity,
        location: formData.location.trim() || undefined,
        equipment: formData.equipment,
        isActive: formData.isActive,
        virtualLink: formData.virtualLink.trim() || undefined,
        virtualRoomId: formData.virtualRoomId.trim() || undefined
      };

      const newRoom = await roomsApi.create(eventId, roomData);
      setRooms(prev => [...prev, newRoom]);
      setShowCreateModal(false);
      resetForm();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setFormLoading(false);
    }
  };

  // Handle edit room
  const handleEditRoom = async () => {
    if (!editingRoom) return;

    try {
      setFormLoading(true);
      setError(null);

      const roomData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        capacity: formData.capacity,
        location: formData.location.trim() || undefined,
        equipment: formData.equipment,
        isActive: formData.isActive,
        virtualLink: formData.virtualLink.trim() || undefined,
        virtualRoomId: formData.virtualRoomId.trim() || undefined
      };

      const updatedRoom = await roomsApi.update(editingRoom.id.toString(), roomData);
      setRooms(prev => prev.map(room => room.id === editingRoom.id ? updatedRoom : room));
      setEditingRoom(null);
      resetForm();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setFormLoading(false);
    }
  };

  // Handle delete room
  const handleDeleteRoom = async () => {
    if (!deletingRoom) return;

    try {
      setFormLoading(true);
      setError(null);

      console.log('Deleting room:', deletingRoom.id);
      await roomsApi.delete(deletingRoom.id.toString());
      console.log('Room deleted successfully');
      setRooms(prev => prev.filter(room => room.id !== deletingRoom.id));
      setDeletingRoom(null);
    } catch (err) {
      console.error('Delete room error:', err);
      setError(handleApiError(err));
    } finally {
      setFormLoading(false);
    }
  };

  // Open edit modal
  const openEditModal = (room: Room) => {
    setEditingRoom(room);
    setFormData({
      name: room.name,
      description: room.description || '',
      capacity: room.capacity,
      location: room.location || '',
      equipment: room.equipment,
      isActive: room.isActive,
      virtualLink: room.virtualLink || '',
      virtualRoomId: room.virtualRoomId || ''
    });
  };

  // Close modals
  const closeModals = () => {
    setShowCreateModal(false);
    setEditingRoom(null);
    resetForm();
    setError(null);
  };

  const isFormValid = formData.name.trim() && formData.capacity >= 1;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Rooms & Spaces</h3>
          <p className="text-sm text-gray-600">
            Configure physical and virtual spaces for your event
          </p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)} 
          className="flex items-center bg-blue-600 hover:bg-blue-700 text-white border-blue-600 hover:border-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Room
        </Button>
      </div>

      {error && (
        <Alert type="error" title="Error" message={error} />
      )}

      {rooms.length === 0 ? (
        <EmptyState
          title="No rooms configured"
          description="Add rooms and spaces to organize your event effectively."
          action={{
            label: "Add First Room",
            onClick: () => setShowCreateModal(true)
          }}
        />
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {rooms.map((room) => (
              <li key={room.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">{room.name}</h4>
                        {room.description && (
                          <p className="text-sm text-gray-600 mt-1">{room.description}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={room.isActive ? 'success' : 'default'}>
                          {room.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                        {room.virtualLink ? (
                          <Badge variant="info">
                            <Monitor className="w-3 h-3 mr-1" />
                            Virtual
                          </Badge>
                        ) : (
                          <Badge variant="default">
                            <Building2 className="w-3 h-3 mr-1" />
                            Physical
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-6 mt-3 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Users className="w-4 h-4 mr-1" />
                        Capacity: {room.capacity}
                      </div>
                      {room.location && (
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          {room.location}
                        </div>
                      )}
                    </div>

                    {room.equipment.length > 0 && (
                      <div className="mt-3">
                        <div className="flex flex-wrap gap-1">
                          {room.equipment.slice(0, 6).map((item: string, index: number) => (
                            <Badge key={index} variant="default" size="sm">
                              {item}
                            </Badge>
                          ))}
                          {room.equipment.length > 6 && (
                            <Badge variant="default" size="sm">
                              +{room.equipment.length - 6} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditModal(room)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        console.log('Delete button clicked for room:', room.id, room.name);
                        // Close any open modals first
                        setShowCreateModal(false);
                        setEditingRoom(null);
                        setDeletingRoom(room);
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Create/Edit Room Modal */}
      <Modal
        isOpen={showCreateModal || !!editingRoom}
        onClose={closeModals}
        title={editingRoom ? 'Edit Room' : 'Add New Room'}
        size="lg"
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Room Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Conference Room A"
              />
              {!formData.name.trim() && (
                <p className="text-red-500 text-sm mt-1">Room name is required</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Capacity <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                value={formData.capacity}
                onChange={(e) => handleInputChange('capacity', parseInt(e.target.value) || 1)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Brief description of the room..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Building A, Floor 2"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Virtual Meeting Link</label>
              <input
                type="url"
                value={formData.virtualLink}
                onChange={(e) => handleInputChange('virtualLink', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://meet.google.com/..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Virtual Room ID</label>
              <input
                type="text"
                value={formData.virtualRoomId}
                onChange={(e) => handleInputChange('virtualRoomId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Meeting ID or Room Code"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Equipment & Amenities</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {equipmentOptions.map((equipment) => (
                <label key={equipment} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.equipment.includes(equipment)}
                    onChange={() => handleEquipmentToggle(equipment)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{equipment}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Room is active and available for booking</span>
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button variant="secondary" onClick={closeModals}>
              Cancel
            </Button>
            <Button
              onClick={editingRoom ? handleEditRoom : handleCreateRoom}
              disabled={!isFormValid || formLoading}
              loading={formLoading}
            >
              {editingRoom ? 'Update Room' : 'Create Room'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deletingRoom}
        onClose={() => {
          console.log('ConfirmDialog onClose called');
          setDeletingRoom(null);
        }}
        onConfirm={() => {
          console.log('ConfirmDialog onConfirm called');
          handleDeleteRoom();
        }}
        title="Delete Room"
        message={`Are you sure you want to delete "${deletingRoom?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        loading={formLoading}
      />
    </div>
  );
};

export default RoomsConfiguration;
